// HTTP client for dash-api's agentic (plan -> execute -> answer) AI Assistant Chat endpoints
// (MINSKY-16473 ticket 13 — frontend cutover). Prior to this ticket this module pointed at a
// separate standalone Node.js service with its own base URL and no `/api` prefix
// (`REACT_APP_AI_ASSISTANT_SERVICE_URL`, defaulting to `http://localhost:8080`); ticket 12 ported
// that service's `POST /runs`/`GET /runs/:id/stream`/`GET|DELETE /conversations[/:id]` contract
// (request/response shapes verbatim) onto dash-api itself, under `/api/runs` and
// `/api/conversations` (see `AiAgenticRunController`/`AiRunController`/`AiConversationController`
// in dash-api's `com.awp.aichat.controller` package). There is no longer a second service to have
// a separate base URL for, so this client now shares `../api/chatApi.js`'s
// `AI_ASSISTANT_CHAT_API_BASE` (the one dash-api base URL every other client in this feature
// already uses) rather than the retired `AI_ASSISTANT_SERVICE_URL`/
// `REACT_APP_AI_ASSISTANT_SERVICE_URL`.
//
// Auth: identical bearer JWT as every other dash-api call in this app — `getAwpClientHeaders()`
// already includes `Authorization: Bearer <token>` from the same in-memory session
// (`src/auth/jwtAuth.js`) used everywhere else.
import {getAwpClientHeaders} from '../../../../session/awpRequestHeaders';
import {AI_ASSISTANT_CHAT_API_BASE} from '../api/chatApi';

function resolveBaseUrl(apiBaseUrl) {
    return apiBaseUrl || AI_ASSISTANT_CHAT_API_BASE;
}

async function parseResponseBody(response) {
    const text = await response.text();
    if (!text) {
        return null;
    }
    try {
        return JSON.parse(text);
    } catch (_) {
        return text;
    }
}

function buildErrorMessage(fallbackMessage, payload) {
    if (typeof payload === 'string' && payload.trim()) {
        return `${fallbackMessage}: ${payload}`;
    }
    const apiMessage = payload?.error?.message || payload?.error || payload?.message;
    return apiMessage ? `${fallbackMessage}: ${apiMessage}` : fallbackMessage;
}

/** Builds the `GET /api/runs/:runId/stream` URL. Exported so `streamAgenticRun.js` (and tests)
 * don't need to duplicate the URL shape. */
export function buildAgenticRunStreamUrl(runId, apiBaseUrl) {
    return `${resolveBaseUrl(apiBaseUrl)}/api/runs/${encodeURIComponent(runId)}/stream`;
}

/**
 * `POST /api/runs` (`AiAgenticRunController#startRun`) — starts a new agentic run for `goal` and
 * returns immediately (the backend responds `202` before the run finishes; progress/completion
 * arrive over `streamAgenticRun`/`GET /api/runs/:runId/stream`).
 *
 * @param {string} goal
 * @param {string} [apiBaseUrl] — overrides `AI_ASSISTANT_CHAT_API_BASE` (mainly for tests).
 * @param {string} [conversationId] — MINSKY-16655/16657: continue an existing conversation
 *   (multi-turn) instead of starting a brand-new one. The backend's `POST /api/runs` accepts this
 *   as an optional body field — omitting it (every caller before this parameter existed)
 *   preserves prior behavior exactly. The frontend generates this id itself (see
 *   `useAgenticRunController.js`) rather than waiting for the async persistence step to report one
 *   back, since `POST /api/runs` only ever returns `runId`.
 * @param {{url?: string, chart?: {chartId: string, title?: string, linkedProducts?: Array<{productId: string, title?: string, productType?: string}>}, documents?: Array<object>}} [context] — page URL,
 *   optional chart grounding (`/whiteboards/:id`, including linked product reports), and document list for the backend request-context parser.
 *   Omitting it preserves prior behavior for callers that only pass goal/conversationId.
 * @returns {Promise<{runId: string}>}
 */
export async function startAgenticRun(goal, apiBaseUrl, conversationId, context) {
    const trimmedGoal = typeof goal === 'string' ? goal.trim() : '';
    if (!trimmedGoal) {
        throw new Error('startAgenticRun requires a non-empty goal');
    }

    const body = {goal: trimmedGoal};
    if (typeof conversationId === 'string' && conversationId.length > 0) {
        body.conversationId = conversationId;
    }
    if (context && typeof context === 'object') {
        body.context = context;
    }

    const response = await fetch(`${resolveBaseUrl(apiBaseUrl)}/api/runs`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...getAwpClientHeaders(),
        },
        body: JSON.stringify(body),
    });

    const payload = await parseResponseBody(response);

    if (!response.ok) {
        throw new Error(buildErrorMessage(`Failed to start agentic run (${response.status})`, payload));
    }

    if (!payload?.runId) {
        throw new Error('Agentic run started without a runId');
    }

    return {runId: payload.runId};
}

/**
 * `GET /api/conversations` (`AiConversationController#listConversations`) — every conversation
 * owned by the current user, newest-updated first. Response shape
 * (`{id, title, createdAt, updatedAt, metadata, userId}[]`) matches the legacy `chatApi.js`'s
 * `listConversations` shape, so `ThreadHistory.jsx`/`GlobalAiAssistantPanel.jsx` need no shape
 * translation.
 *
 * @param {string} [apiBaseUrl]
 * @returns {Promise<Array<{id: string, title: string, createdAt: string, updatedAt: string, metadata: object}>>}
 */
export async function listAgenticConversations(apiBaseUrl) {
    const response = await fetch(`${resolveBaseUrl(apiBaseUrl)}/api/conversations`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            ...getAwpClientHeaders(),
        },
    });
    const payload = await parseResponseBody(response);
    if (!response.ok) {
        throw new Error(buildErrorMessage(`Failed to list conversations (${response.status})`, payload));
    }
    return Array.isArray(payload) ? payload : [];
}

/**
 * `GET /api/conversations/:id` (`AiConversationController#getConversation`) — the conversation
 * plus its full messages/runs history. Used to resume a past thread (see
 * `useAgenticRunController.js`'s `loadConversation`).
 *
 * @param {string} conversationId
 * @param {string} [apiBaseUrl]
 * @returns {Promise<{conversation: object, messages: Array<object>, runs: Array<object>}>}
 */
export async function getAgenticConversation(conversationId, apiBaseUrl) {
    const response = await fetch(`${resolveBaseUrl(apiBaseUrl)}/api/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            ...getAwpClientHeaders(),
        },
    });
    const payload = await parseResponseBody(response);
    if (!response.ok) {
        throw new Error(buildErrorMessage(`Failed to load conversation (${response.status})`, payload));
    }
    return payload;
}

/**
 * `DELETE /api/conversations/:id` (`AiConversationController#deleteConversation`) — permanently
 * deletes a conversation and its messages/runs. Resolves with no value on success (`204`); throws
 * otherwise, mirroring `chatApi.js`'s `deleteConversation` error-handling convention exactly.
 *
 * @param {string} conversationId
 * @param {string} [apiBaseUrl]
 * @returns {Promise<void>}
 */
export async function deleteAgenticConversation(conversationId, apiBaseUrl) {
    const response = await fetch(`${resolveBaseUrl(apiBaseUrl)}/api/conversations/${encodeURIComponent(conversationId)}`, {
        method: 'DELETE',
        headers: {...getAwpClientHeaders()},
    });
    if (!response.ok && response.status !== 204) {
        const payload = await parseResponseBody(response);
        throw new Error(buildErrorMessage(`Failed to delete conversation (${response.status})`, payload));
    }
}

/**
 * `POST /api/runs/:runId/feedback` — upsert or remove a thumbs rating for a run.
 *
 * @param {string} runId
 * @param {'thumbs_up'|'thumbs_down'|null} rating — null removes the rating
 * @param {string} [apiBaseUrl]
 * @returns {Promise<void>}
 */
export async function submitRunFeedback(runId, rating, apiBaseUrl) {
    const response = await fetch(`${resolveBaseUrl(apiBaseUrl)}/api/runs/${encodeURIComponent(runId)}/feedback`, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
            Accept: 'application/json',
            ...getAwpClientHeaders(),
        },
        body: JSON.stringify({rating: rating ?? null}),
    });
    if (!response.ok && response.status !== 204) {
        const payload = await parseResponseBody(response);
        throw new Error(buildErrorMessage(`Failed to submit run feedback (${response.status})`, payload));
    }
}

/**
 * `GET /api/ai-assistant/chart/config` — effective Pure LLM chart AI config for the UI
 *
 * @param {string} [apiBaseUrl]
 * @returns {Promise<{pureLlmEnabled: boolean}>}
 */
export async function getChartAiConfig(apiBaseUrl) {
    const response = await fetch(`${resolveBaseUrl(apiBaseUrl)}/api/ai-assistant/chart/config`, {
        method: 'GET',
        headers: {
            Accept: 'application/json',
            ...getAwpClientHeaders(),
        },
    });
    const payload = await parseResponseBody(response);
    if (!response.ok) {
        throw new Error(buildErrorMessage(`Failed to load chart AI config (${response.status})`, payload));
    }
    return {
        pureLlmEnabled: Boolean(payload?.pureLlmEnabled),
    };
}
